import React, { useState, useMemo, useEffect } from "react";
import { View, ScrollView, TextInput, Pressable, StyleSheet } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { ContactType } from "@/types/db/contacts";
import { contactService } from "@/services/contactService";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { supabase } from "@/lib/supabase-client";

// Discriminated union for type safety
export type ContactPickerProps =
  | {
      clientId?: string;
      excludeContactIds?: string[];
      title?: string;
      pickMany?: false;
      onPick: (contact: ContactType) => void;
      onCancel?: () => void;
    }
  | {
      clientId?: string;
      excludeContactIds?: string[];
      title?: string;
      pickMany: true;
      selectedContactIds?: string[];
      onPick: (contacts: ContactType[]) => void;
      onCancel?: () => void;
    };

export function ContactPicker(props: ContactPickerProps) {
  const { clientId, excludeContactIds = [], title, onPick, onCancel, pickMany } = props;
  const { colors } = useTheme();
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<ContactType[]>([]);
  const [clientContactIds, setClientContactIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  // State for multi-select mode
  const [selectedContactIds, setSelectedContactIds] = useState<string[]>(
    pickMany && props.selectedContactIds ? props.selectedContactIds : [],
  );

  // Toggle function for multi-select
  const handleContactToggle = (contactId: string) => {
    if (!pickMany) return;
    setSelectedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId],
    );
  };

  // Confirm handler for multi-select
  const handleConfirm = () => {
    if (!pickMany) return;
    const selectedContacts = contacts.filter((contact) => contact.id && selectedContactIds.includes(contact.id));
    onPick(selectedContacts);
  };

  // Fetch contacts on mount
  useEffect(() => {
    const fetchContacts = async () => {
      setLoading(true);
      try {
        const data = await contactService.getContacts();
        setContacts(data);
      } catch (error) {
        console.error("Failed to fetch contacts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchContacts();
  }, []);

  // Fetch client contact IDs from link table when clientId is provided
  useEffect(() => {
    const fetchClientContactIds = async () => {
      if (!clientId) {
        setClientContactIds([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .schema("link")
          .from("clients_contacts")
          .select("contact_id")
          .eq("id", clientId);

        if (error) {
          console.error("Failed to fetch client contacts:", error);
          setClientContactIds([]);
          return;
        }

        setClientContactIds(data?.map((d) => d.contact_id) || []);
      } catch (error) {
        console.error("Failed to fetch client contacts:", error);
        setClientContactIds([]);
      }
    };
    fetchClientContactIds();
  }, [clientId]);

  // Categorize and filter contacts
  const { clientContacts, otherContacts } = useMemo(() => {
    // First exclude any contacts in excludeContactIds
    let filtered =
      excludeContactIds.length > 0 ? contacts.filter((c) => c.id && !excludeContactIds.includes(c.id)) : contacts;

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter((contact) => {
        const displayName = contact.meta_data?.preferred_name || contact.first_name;
        const fullName = `${displayName} ${contact.last_name}`.toLowerCase();
        const email = contact.email_address?.toLowerCase() || "";
        const phone = contact.phone_number?.toLowerCase() || "";

        return fullName.includes(searchLower) || email.includes(searchLower) || phone.includes(searchLower);
      });
    }

    // Separate client contacts from other contacts using link table data
    if (clientId && clientContactIds.length > 0) {
      const clientContactsList = filtered.filter((c) => c.id && clientContactIds.includes(c.id));
      const otherContactsList = filtered.filter((c) => c.id && !clientContactIds.includes(c.id));
      return {
        clientContacts: clientContactsList,
        otherContacts: otherContactsList,
      };
    }

    return {
      clientContacts: [],
      otherContacts: filtered,
    };
  }, [contacts, search, clientId, clientContactIds, excludeContactIds]);

  const renderContact = (contact: ContactType) => {
    const displayName = contact.meta_data?.preferred_name || contact.first_name;
    const fullName = `${displayName} ${contact.last_name}`;
    const contactId = contact.id ?? "";
    const isSelected = pickMany && selectedContactIds.includes(contactId);

    return (
      <Pressable
        key={contactId}
        testID={`contact-option-${contactId}`}
        style={[
          styles.contactRow,
          { borderBottomColor: colors.border },
          isSelected && pickMany && { backgroundColor: colors.backgroundHover },
        ]}
        onPress={() => (pickMany ? handleContactToggle(contactId) : onPick(contact))}
      >
        <View style={[styles.contactContent, styles.contactContentWithCheckbox]}>
          {pickMany && (
            <MaterialCommunityIcons
              name={isSelected ? "checkbox-marked" : "checkbox-blank-outline"}
              size={20}
              color={isSelected ? colors.primary : colors.textSecondary}
              style={styles.checkbox}
            />
          )}
          <Avatar
            email={contact.email_address || undefined}
            firstName={displayName || undefined}
            lastName={contact.last_name || undefined}
            size={36}
          />
          <View style={{ flex: 1 }}>
            <ThemedText type="defaultSemiBold">{fullName}</ThemedText>
            {contact.email_address && <ThemedText style={styles.contactDetail}>{contact.email_address}</ThemedText>}
            {contact.phone_number && <ThemedText style={styles.contactDetail}>{contact.phone_number}</ThemedText>}
          </View>
        </View>
      </Pressable>
    );
  };

  return (
    <View testID="contact-picker" style={styles.container}>
      <ThemedText type="subtitle">{title || (pickMany ? "Select Contacts" : "Select Contact")}</ThemedText>

      <TextInput
        testID="contact-picker-search"
        style={[
          styles.searchInput,
          {
            borderColor: colors.border,
            color: colors.text,
            backgroundColor: colors.backgroundSecondary,
          },
        ]}
        placeholder="Search by name, email, or phone..."
        placeholderTextColor={colors.placeholder}
        value={search}
        onChangeText={setSearch}
        autoFocus
      />

      <ScrollView style={styles.scrollView}>
        {loading ? (
          <ThemedText style={styles.message}>Loading contacts...</ThemedText>
        ) : (
          <>
            {/* Client Contacts Section */}
            {clientId && clientContacts.length > 0 && (
              <>
                <View style={[styles.sectionHeader, { backgroundColor: colors.backgroundSecondary }]}>
                  <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                    Client Contacts
                  </ThemedText>
                </View>
                {clientContacts.map(renderContact)}
              </>
            )}

            {/* All Contacts Section */}
            {otherContacts.length > 0 && (
              <>
                {clientId && clientContacts.length > 0 && (
                  <View style={[styles.sectionHeader, { backgroundColor: colors.backgroundSecondary }]}>
                    <ThemedText type="defaultSemiBold" style={styles.sectionTitle}>
                      All Contacts
                    </ThemedText>
                  </View>
                )}
                {otherContacts.map(renderContact)}
              </>
            )}

            {/* No results message */}
            {clientContacts.length === 0 && otherContacts.length === 0 && (
              <ThemedText style={styles.message}>{search ? "No contacts found" : "No contacts available"}</ThemedText>
            )}
          </>
        )}
      </ScrollView>

      {pickMany ? (
        <View style={styles.footer}>
          <ThemedText style={{ fontSize: 14, opacity: 0.7, marginRight: "auto" }}>
            {selectedContactIds.length} selected
          </ThemedText>
          {onCancel && (
            <Button
              testID="contact-picker-cancel"
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          )}
          <Button
            testID="contact-picker-confirm"
            text="Confirm"
            style={[
              styles.confirmButton,
              {
                backgroundColor: selectedContactIds.length > 0 ? colors.primary : colors.backgroundSecondary,
                opacity: selectedContactIds.length > 0 ? 1 : 0.5,
              },
            ]}
            textStyle={{ color: colors.buttonText }}
            enabled={selectedContactIds.length > 0}
            onPress={handleConfirm}
          />
        </View>
      ) : (
        onCancel && (
          <View style={styles.footer}>
            <Button
              testID="contact-picker-cancel"
              text="Cancel"
              style={[styles.cancelButton, { borderColor: colors.border }]}
              textStyle={{ color: colors.text }}
              onPress={onCancel}
            />
          </View>
        )
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    padding: 8,
    gap: 8,
  },
  searchInput: {
    height: 36,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
  },
  scrollView: {
    maxHeight: 350,
  },
  sectionHeader: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginTop: 8,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 12,
    textTransform: "uppercase",
    opacity: 0.8,
  },
  contactRow: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    backgroundColor: "transparent",
  },
  contactContent: {
    gap: 2,
  },
  contactContentWithCheckbox: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  checkbox: {
    marginTop: 2,
  },
  contactDetail: {
    fontSize: 12,
    opacity: 0.6,
  },
  message: {
    opacity: 0.7,
    alignSelf: "center",
    marginVertical: 20,
  },
  footer: {
    flexDirection: "row",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 8,
  },
  cancelButton: {
    height: 32,
    paddingHorizontal: 16,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  confirmButton: {
    height: 32,
    paddingHorizontal: 16,
  },
});
